import { IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { connect } from 'react-redux';
import { IState } from '../../../store';
import * as Formatters from '../../../utils/bargain_terms_formatter';
import { FOfferType, getOfferType } from '../../../utils/offer_type';
import { BargainTermsBlock, IBargainTermsBlockProps } from './bargain_terms_group';
import { trackOpenPhones } from './tracking';

function mapCoworkingRentTerms(offer: IOfferSchema): [string, string | string[]][] {
  const {
    bargainTerms: { includedOptions, minLeaseTerm, priceForWorkplace },
    totalArea,
    floorNumber,
    building,
    coworking,
  } = offer;

  const terms: [string, string | string[]][] = [
    ['Цена за рабочее место', Formatters.formatPriceForWorkplace(priceForWorkplace)],
    ['Цена за весь офис', Formatters.priceFormatter(offer) || '-'],
    ['Коммунальные платежи', Formatters.utilityCharges(includedOptions)],
    ['Эксплуатационные расходы', Formatters.operationalCosts(includedOptions)],
    ['Срок аренды', Formatters.formatMinLeaseTerm(minLeaseTerm)],
    ['Площадь', Formatters.formatTotalArea(totalArea)],
    ['Этаж', Formatters.formatFloor(floorNumber, building)],
    ['Доступ', Formatters.formatAccessTime(coworking)],
  ];

  return terms;
}

function mapRentTerms(offer: IOfferSchema, withAdditions?: boolean): [string, string | string[]][] {
  if (offer.coworkingOfferType) {
    return mapCoworkingRentTerms(offer);
  }

  const {
    bargainTerms,
    building,
    vatPriceTotalPerMonthRur,
    minVatPriceTotalPerMonthRur,
    pricesPerSquareMeterPerYear,
  } = offer;

  const {
    vatType,
    leaseTermType,
    hasGracePeriod,
    includedOptions,
    prepayMonths,
    agentBonus,
    clientFee,
    securityDepositPrices,
    agentFee,
    minLeaseTerm,
  } = bargainTerms;

  const vat = vatPriceTotalPerMonthRur;
  const pricesPerSquareMeterPerYearRur = pricesPerSquareMeterPerYear ? pricesPerSquareMeterPerYear.rur : null;
  const isAreaParts = (offer.areaParts && offer.areaParts.length > 0) || false;

  let terms: [string, string | string[]][] = [
    ['Цена', Formatters.priceFormatter(offer) || '-'],
    ['Ставка', Formatters.AreaPerMetterFormatter(pricesPerSquareMeterPerYearRur, isAreaParts)],
    ['Налог', Formatters.vatTypeFormatter(vatType, vat, minVatPriceTotalPerMonthRur)],
    ['Обеспечительный платеж', Formatters.securityDeposites(securityDepositPrices)],
    ['Коммунальные платежи', Formatters.utilityCharges(includedOptions)],
    ['Эксплуатационные расходы', Formatters.operationalCosts(includedOptions)],
    ['Предоплата', Formatters.prepayMonths(prepayMonths)],
    ['Тип аренды', Formatters.leaseTypeFormatter(bargainTerms.leaseType)],
    ['Срок аренды', Formatters.leaseTermTypeFormatter(leaseTermType)],
    ['Минимальный срок аренды', minLeaseTerm ? `${minLeaseTerm} мес.` : '-'],
    ['Арендные каникулы', Formatters.hasGracePeriodFormatter(hasGracePeriod)],
    ['Стоимость парковки', Formatters.parkingFormatter(building ? building.parking : null)],
    ['Комиссия от клиента', Formatters.clientFee(clientFee)],
  ];

  const addTerms: [string, string | string[]][] = [
    ['Комиссия от агента', Formatters.agentFee(agentFee)],
    ['Бонус агенту', Formatters.agentBonus(agentBonus)],
  ];

  if (getOfferType(offer) === FOfferType.Garage) {
    terms = terms.filter(([name]) => name !== 'Налог');
  }

  return withAdditions ? terms.concat(addTerms) : terms;
}

function mapSaleTerms(offer: IOfferSchema, withAdditions?: boolean) {
  const { bargainTerms, vatPriceTotalRur, minVatPriceTotalRur, pricesPerSquareMeter } = offer;

  const { vatType, agentBonus, contractType } = bargainTerms;

  const pricesPerSquareMeterRur = pricesPerSquareMeter ? pricesPerSquareMeter.rur : null;
  const isAreaParts = (offer.areaParts && offer.areaParts.length > 0) || false;

  let terms: [string, string | string[]][] = [
    ['Цена', Formatters.priceFormatter(offer) || '-'],
    ['Ставка', Formatters.AreaPerUnitFormatter(pricesPerSquareMeterRur, isAreaParts)],
    ['Налог', Formatters.vatTypeFormatter(vatType, vatPriceTotalRur, minVatPriceTotalRur)],
    ['Тип сделки', Formatters.formatPPA(contractType)],
  ];

  if (getOfferType(offer) === FOfferType.Garage) {
    terms = terms.filter(([name]) => name !== 'Налог');
  }

  const addTerms: [string, string | string[]][] = [['Бонус агенту', Formatters.agentBonus(agentBonus)]];

  return withAdditions ? terms.concat(addTerms) : terms;
}

export function mapStateToProps(state: IState): IBargainTermsBlockProps {
  const {
    offerData: { user, offer, kp, agent },
  } = state;
  const isAuthorised = user && user.isAuthenticated && user.isAgent;
  const kpId = kp && kp.id;

  const isRent = offer.dealType === 'rent';
  const headerTitle = offer.coworkingOfferType ? 'Условия аренды' : 'Условия сделки';

  return {
    phones: offer.phones || [],
    sendCa: (isBot?: boolean) => trackOpenPhones(offer, agent, kpId, isBot),
    terms: isRent ? mapRentTerms(offer, !!isAuthorised) : mapSaleTerms(offer, !!isAuthorised),
    knAuctionCompanies: state.knAuctionCompanies,
    publishedUserId: offer.publishedUserId,
    headerTitle,
    isCoworking: !!offer.coworkingOfferType,
  };
}

export const BargainTermsBlockContainer = connect<IBargainTermsBlockProps>(mapStateToProps)(BargainTermsBlock);
